import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import RolePermissionsSummary from "../../../../Components/Studio/TeamPermissions/RolePermissionsSummary";
import { HELP_ARTICLE_INVITE_TEAM, HELP_ARTICLE_USERS } from "../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  formatInviteCooldownSeconds,
  getInviteResendCooldownRemainingMs,
} from "../../../../helpers/inviteCooldown";
import {
  handleInviteShareChoice,
  promptInviteShare,
} from "../../../../helpers/inviteShare";
import { getUnits, Unit } from "../../../../services/unitService";
import {
  createProfessional,
  deleteProfessionalPhoto,
  getProfessionals,
  Professional,
  uploadProfessionalPhoto,
} from "../../../../services/professionalService";
import { resolvePublicStorageUrl } from "../../../../helpers/professionalAvatar";
import ProfessionalPhotoField from "../Professionals/ProfessionalPhotoField";
import {
  deleteUser,
  extractUserRoleNames,
  extractUserUnitIds,
  getUser,
  getUsers,
  inviteUser,
  UpdateUserPayload,
  USER_ROLE_SLUGS,
  updateUser,
  User,
  UserRoleSlug,
} from "../../../../services/userService";
import {
  getTeamPermissions,
  TeamPermissionMatrix,
} from "../../../../services/permissionService";
import { PaginationMeta } from "../../../../types/api";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
} from "../../../../services/tenantSettingService";
import ProfessionalRosterLines from "../Professionals/ProfessionalRosterLines";
import {
  rosterProfilePath,
  rosterSchedulePath,
  rosterUpdateNotice,
  subscribeProfessionalRosterUpdates,
} from "../Professionals/professionalRosterRefresh";
import { getStatus } from "../../../../services/onboardingService";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const UNITS_FETCH_PER_PAGE = 100;
const PROFESSIONALS_FETCH_PER_PAGE = 100;
const PROFESSIONAL_ROLE = "professional";
const DEFAULT_PROFESSIONAL_COLOR = "#119CF1";

type ProfessionalLinkMode = "link" | "create";

interface UserFormValues {
  name: string;
  email: string;
  password: string;
  roles: string[];
  units: number[];
  professional_id: number | null;
}

const emptyFormValues = (): UserFormValues => ({
  name: "",
  email: "",
  password: "",
  roles: [],
  units: [],
  professional_id: null,
});

const mapUserToFormValues = (user: User): UserFormValues => ({
  name: user.name,
  email: user.email,
  password: "",
  roles: extractUserRoleNames(user).slice(0, 1),
  units: extractUserUnitIds(user),
  professional_id: user.professional?.id ?? null,
});

const UsersList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = t("users.pageTitle");

  const [users, setUsers] = useState<User[]>([]);
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]);
  const [availableProfessionals, setAvailableProfessionals] = useState<Professional[]>([]);
  const [permissionMatrix, setPermissionMatrix] = useState<TeamPermissionMatrix | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissionsLoadError, setPermissionsLoadError] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [professionalLinkMode, setProfessionalLinkMode] =
    useState<ProfessionalLinkMode>("create");
  const [pendingCreatePhoto, setPendingCreatePhoto] = useState<File | null>(null);
  const [pendingCreatePhotoPreview, setPendingCreatePhotoPreview] = useState<
    string | null
  >(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pendingInitialRole, setPendingInitialRole] = useState<string | null>(null);
  const [resendInviteUserId, setResendInviteUserId] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [tenantSettings, setTenantSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);

  useEffect(() => {
    getSettings()
      .then((response) => setTenantSettings(response.data))
      .catch(() => {
        // Default keeps everyone performing every service.
      });
  }, []);

  useEffect(() => {
    const hasPendingInvite = users.some((user) => user.access_status === "pending");
    if (!hasPendingInvite) {
      return;
    }

    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [users]);

  const roleOptions = useMemo(
    () =>
      USER_ROLE_SLUGS.filter((slug) => editingUser || slug !== "owner").map((slug) => ({
        value: slug,
        label: t(`users.fields.roles.options.${slug}`),
      })),
    [t, editingUser]
  );

  const linkableProfessionals = useMemo(() => {
    const currentUserId = editingUser?.id ?? null;

    return availableProfessionals.filter(
      (professional) =>
        professional.user_id === null || professional.user_id === currentUserId
    );
  }, [availableProfessionals, editingUser]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("users.validation.nameRequired")),
        email: Yup.string()
          .trim()
          .email(t("users.validation.emailInvalid"))
          .required(t("users.validation.emailRequired")),
        password: editingUser
          ? Yup.string().min(8, t("users.validation.passwordMin"))
          : Yup.string(),
        roles: Yup.array()
          .of(Yup.string())
          .length(1, t("users.validation.rolesRequired")),
        professional_id: Yup.number()
          .nullable()
          .when("roles", {
            is: (roles: string[]) =>
              roles?.includes(PROFESSIONAL_ROLE) &&
              professionalLinkMode === "link" &&
              !editingUser,
            then: (schema) =>
              schema.required(t("studio.users.validation.professionalRequired")),
            otherwise: (schema) => schema.nullable(),
          }),
        units: Yup.array()
          .of(Yup.number())
          .min(1, t("users.validation.unitsRequired")),
      }),
    [t, editingUser, professionalLinkMode]
  );

  useEffect(() => {
    if (!pendingCreatePhoto) {
      setPendingCreatePhotoPreview(null);
      return;
    }

    const url = URL.createObjectURL(pendingCreatePhoto);
    setPendingCreatePhotoPreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pendingCreatePhoto]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchUsers = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }

    try {
      const response = await getUsers({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setUsers(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      if (!options?.silent) {
        toast.error(error?.data || t("users.toast.loadError"));
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [page, debouncedSearch, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    return subscribeProfessionalRosterUpdates((source, name, kind, professionalId) => {
      void fetchUsers({ silent: true });

      if (source !== "other-tab") {
        return;
      }

      const notice = rosterUpdateNotice(name, kind);
      const message = notice.name ? t(notice.key, { name: notice.name }) : t(notice.key);

      toast.info(
        <span>
          {message}
          {professionalId ? (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => navigate(rosterProfilePath(professionalId, kind))}
              >
                {t("studio.professionals.routine.openProfile")}
              </button>
              {kind === "routine" ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => navigate(rosterSchedulePath(professionalId))}
                  >
                    {t("studio.professionals.routine.openSchedule")}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
        </span>,
        { closeOnClick: false }
      );
    });
  }, [fetchUsers, navigate, t]);

  const loadActiveUnits = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const response = await getUnits({ per_page: UNITS_FETCH_PER_PAGE });
      setAvailableUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("users.toast.unitsLoadError"));
      setAvailableUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, [t]);

  const loadActiveProfessionals = useCallback(async () => {
    setLoadingProfessionals(true);
    try {
      const response = await getProfessionals({ per_page: PROFESSIONALS_FETCH_PER_PAGE });
      setAvailableProfessionals(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.users.toast.professionalsLoadError"));
      setAvailableProfessionals([]);
    } finally {
      setLoadingProfessionals(false);
    }
  }, [t]);

  const loadPermissionMatrix = useCallback(async () => {
    setLoadingPermissions(true);
    setPermissionsLoadError(false);

    try {
      const response = await getTeamPermissions();
      setPermissionMatrix(response.data);
    } catch {
      setPermissionMatrix(null);
      setPermissionsLoadError(true);
    } finally {
      setLoadingPermissions(false);
    }
  }, []);

  useEffect(() => {
    if (offcanvasOpen) {
      loadActiveUnits();
      loadActiveProfessionals();
      loadPermissionMatrix();
    }
  }, [offcanvasOpen, loadActiveUnits, loadActiveProfessionals, loadPermissionMatrix]);

  const openCreateWithRole = useCallback((roleSlug?: string) => {
    setEditingUser(null);
    setFormLoading(false);
    setProfessionalLinkMode("create");
    setPendingCreatePhoto(null);
    setPendingInitialRole(
      roleSlug && USER_ROLE_SLUGS.includes(roleSlug as (typeof USER_ROLE_SLUGS)[number])
        ? roleSlug
        : null
    );
    setOffcanvasOpen(true);
  }, []);

  useEffect(() => {
    const action = searchParams.get("action");
    const role = searchParams.get("role");

    if (action !== "create") {
      return;
    }

    openCreateWithRole(role ?? undefined);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("action");
    nextParams.delete("role");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, openCreateWithRole]);

  const formInitialValues = useMemo(
    () => (editingUser ? mapUserToFormValues(editingUser) : emptyFormValues()),
    [editingUser]
  );

  const formik = useFormik<UserFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      try {
        if (editingUser) {
          const payload: UpdateUserPayload = {
            name: values.name.trim(),
            email: values.email.trim(),
            roles: values.roles,
            units: values.units,
            professional_id: values.roles.includes(PROFESSIONAL_ROLE)
              ? values.professional_id
              : null,
          };

          if (values.password.trim()) {
            payload.password = values.password;
          }

          const response = await updateUser(editingUser.id, payload);
          toast.success(response.message);
        } else {
          const role = values.roles[0];

          if (!role || role === "owner") {
            toast.error(t("users.validation.rolesRequired"));
            return;
          }

          let professionalIdForInvite =
            values.roles.includes(PROFESSIONAL_ROLE) ? values.professional_id : null;
          const creatingInlineProfessional =
            values.roles.includes(PROFESSIONAL_ROLE) &&
            professionalLinkMode === "create";

          if (creatingInlineProfessional) {
            const created = await createProfessional({
              name: values.name.trim(),
              color_hex: DEFAULT_PROFESSIONAL_COLOR,
              email: values.email.trim() || null,
              commission_rate_bps: 0,
            });
            professionalIdForInvite = created.data.id;

            if (pendingCreatePhoto) {
              try {
                await uploadProfessionalPhoto(
                  created.data.id,
                  pendingCreatePhoto
                );
              } catch (photoError: any) {
                toast.error(
                  photoError?.data || t("professionals.toast.photoUploadError")
                );
              }
            }
          }

          const createdInlineProfessionalId =
            creatingInlineProfessional && professionalIdForInvite
              ? professionalIdForInvite
              : null;

          const response = await inviteUser({
            name: values.name.trim(),
            email: values.email.trim(),
            role: role as (typeof USER_ROLE_SLUGS)[number],
            units: values.units,
            professional_id: professionalIdForInvite,
          });
          toast.success(response.message);
          setPendingCreatePhoto(null);
          setProfessionalLinkMode("link");
          closeOffcanvas();

          let wantsInviteAnother = false;
          const inviteUrl = response.data?.invite_url ?? null;
          if (inviteUrl) {
            const shareChoice = await promptInviteShare({
              title: t("users.invite.afterSendTitle"),
              text: t("users.invite.afterSendText", {
                name: values.name.trim(),
                email: values.email.trim(),
              }),
              inviteUrl,
              whatsappMessage: t("users.invite.whatsappMessage", {
                name: values.name.trim(),
                url: inviteUrl,
              }),
              offerInviteAnother: true,
              labels: {
                copy: t("users.invite.copyLink"),
                whatsapp: t("users.invite.whatsapp"),
                sms: t("users.invite.sms"),
                share: t("users.invite.share"),
                done: t("users.invite.done"),
                inviteAnother: t("users.invite.inviteAnother"),
              },
            });

            if (shareChoice === "inviteAnother") {
              wantsInviteAnother = true;
            } else if (shareChoice === "copy") {
              const copied = await handleInviteShareChoice(shareChoice, inviteUrl, {
                copySuccess: t("users.invite.copyLinkSuccess"),
                copyError: t("users.invite.copyLinkError"),
              });
              toast[copied ? "success" : "error"](
                copied
                  ? t("users.invite.copyLinkSuccess")
                  : t("users.invite.copyLinkError")
              );
            }
          } else {
            const inviteAnother = await Swal.fire({
              title: t("users.invite.afterSendTitle"),
              text: t("users.invite.afterSendText", {
                name: values.name.trim(),
                email: values.email.trim(),
              }),
              icon: "success",
              showCancelButton: true,
              confirmButtonText: t("users.invite.inviteAnother"),
              cancelButtonText: t("users.invite.done"),
              confirmButtonColor: "#405189",
              cancelButtonColor: "#878a99",
            });

            if (inviteAnother.isConfirmed) {
              wantsInviteAnother = true;
            }
          }

          if (wantsInviteAnother) {
            openCreateWithRole();
          } else if (createdInlineProfessionalId) {
            let bookingUrl: string | null = null;
            try {
              const status = await getStatus();
              bookingUrl = status.data.quickstart?.booking_url ?? null;
            } catch {
              bookingUrl = null;
            }

            const setHours = await Swal.fire({
              title: t("users.invite.setHoursTitle"),
              text: t("users.invite.setHoursText", {
                name: values.name.trim(),
              }),
              icon: "question",
              showDenyButton: true,
              showCancelButton: true,
              confirmButtonText: t("users.invite.setHoursConfirm"),
              denyButtonText: t("users.invite.openProfile"),
              cancelButtonText: t("users.invite.done"),
              confirmButtonColor: "#405189",
              denyButtonColor: "#0ab39c",
              cancelButtonColor: "#878a99",
              footer: bookingUrl
                ? `<button type="button" class="btn btn-link btn-sm p-0" id="adatis-copy-book">${t(
                    "users.invite.copyBookLink"
                  )}</button>`
                : undefined,
              didOpen: (popup) => {
                const copyBtn = popup.querySelector("#adatis-copy-book");
                if (!copyBtn || !bookingUrl) {
                  return;
                }
                copyBtn.addEventListener("click", () => {
                  void navigator.clipboard
                    .writeText(bookingUrl)
                    .then(() => {
                      toast.success(t("users.invite.copyBookSuccess"));
                    })
                    .catch(() => {
                      toast.error(t("users.invite.copyBookError"));
                    });
                });
              },
            });

            if (setHours.isConfirmed) {
              navigate(rosterSchedulePath(createdInlineProfessionalId));
            } else if (setHours.isDenied) {
              navigate(rosterProfilePath(createdInlineProfessionalId));
            }
          }

          if (page !== 1) {
            setPage(1);
          } else {
            await fetchUsers();
          }
          return;
        }

        closeOffcanvas();

        if (!editingUser && page !== 1) {
          setPage(1);
        } else {
          await fetchUsers();
        }
      } catch (error: any) {
        toast.error(error?.data || t("users.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const selectedRole = formik.values.roles[0] ?? "";
  const isProfessionalRole = selectedRole === PROFESSIONAL_ROLE;
  const { setFieldValue } = formik;

  const selectedProfessional = useMemo(() => {
    if (!isProfessionalRole || formik.values.professional_id == null) {
      return null;
    }

    return (
      availableProfessionals.find(
        (professional) => professional.id === formik.values.professional_id
      ) ?? null
    );
  }, [
    availableProfessionals,
    formik.values.professional_id,
    isProfessionalRole,
  ]);

  const handleUserFormPhotoPick = async (file: File) => {
    if (!selectedProfessional) {
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await uploadProfessionalPhoto(selectedProfessional.id, file);
      const path = response.data?.photo_path ?? null;
      setAvailableProfessionals((current) =>
        current.map((item) =>
          item.id === selectedProfessional.id ? { ...item, photo_path: path } : item
        )
      );
      toast.success(response.message || t("professionals.toast.photoUploadSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.photoUploadError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleUserFormPhotoClear = async () => {
    if (!selectedProfessional?.photo_path) {
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await deleteProfessionalPhoto(selectedProfessional.id);
      setAvailableProfessionals((current) =>
        current.map((item) =>
          item.id === selectedProfessional.id ? { ...item, photo_path: null } : item
        )
      );
      toast.success(response.message || t("professionals.toast.photoRemoveSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.photoRemoveError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  useEffect(() => {
    if (!offcanvasOpen || editingUser || !pendingInitialRole) {
      return;
    }

    setFieldValue("roles", [pendingInitialRole]);
    setPendingInitialRole(null);
  }, [offcanvasOpen, editingUser, pendingInitialRole, setFieldValue]);

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingUser(null);
    setFormLoading(false);
    setPendingInitialRole(null);
    setProfessionalLinkMode("create");
    setPendingCreatePhoto(null);
  };

  const openCreate = () => {
    openCreateWithRole();
  };

  const openEdit = async (user: User) => {
    setEditingUser(null);
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getUser(user.id);
      setEditingUser(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("users.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const selectRole = (slug: string) => {
    formik.setFieldValue("roles", [slug]);
    formik.setFieldTouched("roles", true);

    if (slug !== PROFESSIONAL_ROLE) {
      formik.setFieldValue("professional_id", null);
    }
  };

  const toggleUnit = (unitId: number) => {
    const current = formik.values.units;
    const next = current.includes(unitId)
      ? current.filter((id) => id !== unitId)
      : [...current, unitId];
    formik.setFieldValue("units", next);
    formik.setFieldTouched("units", true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deleteUser(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);

      const isLastItemOnPage = users.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchUsers();
      }
    } catch (error: any) {
      toast.error(error?.data || t("users.toast.deleteError"));
    }
  };

  const handleResendInvite = async (user: User) => {
    const roles = extractUserRoleNames(user);
    const role = roles[0];

    if (!role || role === "owner" || !USER_ROLE_SLUGS.includes(role as UserRoleSlug)) {
      toast.error(t("users.invite.resendError"));
      return;
    }

    const cooldownMs = getInviteResendCooldownRemainingMs(user.invited_at, nowTick);
    if (cooldownMs > 0) {
      toast.info(
        t("users.invite.resendCooldown", {
          seconds: formatInviteCooldownSeconds(cooldownMs),
        })
      );
      return;
    }

    const confirmation = await Swal.fire({
      title: t("users.invite.resendConfirmTitle"),
      text: t("users.invite.resendConfirmText", { name: user.name, email: user.email }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("users.actions.resendInvite"),
      cancelButtonText: t("users.actions.cancel"),
      confirmButtonColor: "#405189",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setResendInviteUserId(user.id);

    try {
      const response = await inviteUser({
        name: user.name,
        email: user.email,
        role: role as UserRoleSlug,
        units: extractUserUnitIds(user),
        professional_id: user.professional?.id ?? null,
      });
      toast.success(response.message || t("users.invite.resendSuccess"));
      const inviteUrl = response.data?.invite_url ?? null;
      if (inviteUrl) {
        const shareChoice = await promptInviteShare({
          title: t("users.invite.copyLinkTitle"),
          text: t("users.invite.copyLinkText"),
          inviteUrl,
          whatsappMessage: t("users.invite.whatsappMessage", {
            name: user.name,
            url: inviteUrl,
          }),
          labels: {
            copy: t("users.invite.copyLink"),
            whatsapp: t("users.invite.whatsapp"),
            sms: t("users.invite.sms"),
            share: t("users.invite.share"),
            done: t("users.invite.done"),
          },
        });

        if (shareChoice === "copy") {
          const copied = await handleInviteShareChoice(shareChoice, inviteUrl, {
            copySuccess: t("users.invite.copyLinkSuccess"),
            copyError: t("users.invite.copyLinkError"),
          });
          toast[copied ? "success" : "error"](
            copied
              ? t("users.invite.copyLinkSuccess")
              : t("users.invite.copyLinkError")
          );
        }
      }
      await fetchUsers();
    } catch (error: any) {
      toast.error(error?.data || t("users.invite.resendError"));
    } finally {
      setResendInviteUserId(null);
    }
  };

  const formatRolesLabel = (user: User): string => {
    const names = extractUserRoleNames(user);
    if (!names.length) {
      return t("users.common.notAvailable");
    }

    return names
      .map((slug) => t(`users.fields.roles.options.${slug}`, { defaultValue: slug }))
      .join(", ");
  };

  const formatUnitsLabel = (user: User): string => {
    if (!user.units?.length) {
      return t("users.common.notAvailable");
    }

    return user.units.map((unit) => unit.name).join(", ");
  };

  const formatLinkedProfessional = (user: User): string => {
    return user.professional?.name ?? t("users.common.notAvailable");
  };

  const totalPages = meta?.last_page ?? 1;
  const hasActiveSearch = debouncedSearch.length > 0;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("users.breadcrumb.title")}
            pageTitle={t("users.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">{t("users.card.title")}</h4>
                  <HelpArticleLink articlePath={HELP_ARTICLE_USERS} className="align-self-center" />
                  <HelpArticleLink articlePath={HELP_ARTICLE_INVITE_TEAM} className="align-self-center" />
                  <Button color="soft-primary" tag={Link} to="/settings/team-permissions">
                    <i className="ri-shield-keyhole-line align-bottom me-1" />
                    {t("users.form.permissions_summary.learnProfilesLink")}
                  </Button>
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("users.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <Row className="mb-3">
                    <Col md={5} lg={4}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder={t("users.search.placeholder")}
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <i className="ri-search-line search-icon" />
                      </div>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">
                        {hasActiveSearch
                          ? t("users.empty.noResults", { term: debouncedSearch })
                          : t("users.empty.noData")}
                      </p>
                      {!hasActiveSearch && (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("users.actions.createFirst")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("users.table.name")}</th>
                              <th>{t("users.table.email")}</th>
                              <th>{t("users.table.roles")}</th>
                              <th>{t("users.list.linked_professional")}</th>
                              <th>{t("users.table.units")}</th>
                              <th className="text-end" style={{ width: 160 }}>
                                {t("users.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => {
                              const accessStatus = user.access_status;
                              const isPendingInvite = accessStatus === "pending";
                              const isResending = resendInviteUserId === user.id;
                              const resendCooldownMs = getInviteResendCooldownRemainingMs(
                                user.invited_at,
                                nowTick
                              );
                              const resendCooldownSeconds =
                                formatInviteCooldownSeconds(resendCooldownMs);
                              const inviteSentAtLabel =
                                user.invited_at &&
                                !Number.isNaN(new Date(user.invited_at).getTime())
                                  ? new Intl.DateTimeFormat(i18n.language, {
                                      dateStyle: "medium",
                                      timeStyle: "short",
                                    }).format(new Date(user.invited_at))
                                  : null;
                              const invitePendingTitle = inviteSentAtLabel
                                ? t("users.invite.sentAt", {
                                    date: inviteSentAtLabel,
                                  })
                                : undefined;
                              const resendTitle =
                                resendCooldownMs > 0
                                  ? t("users.invite.resendCooldown", {
                                      seconds: resendCooldownSeconds,
                                    })
                                  : t("users.actions.resendInvite");

                              return (
                              <tr key={user.id}>
                                <td className="fw-medium">
                                  <div className="d-flex flex-wrap align-items-center gap-1">
                                    <span>{user.name}</span>
                                    {accessStatus === "active" ? (
                                      <Badge color="success" className="badge-soft-success">
                                        {t("users.badges.has_access")}
                                      </Badge>
                                    ) : accessStatus === "pending" ? (
                                      <Badge
                                        color="warning"
                                        className="badge-soft-warning"
                                        title={invitePendingTitle}
                                      >
                                        {t("users.badges.invite_pending")}
                                      </Badge>
                                    ) : null}
                                  </div>
                                </td>
                                <td>{user.email}</td>
                                <td className="text-muted">{formatRolesLabel(user)}</td>
                                <td className="text-muted" style={{ whiteSpace: "normal" }}>
                                  <div>{formatLinkedProfessional(user)}</div>
                                  {user.professional ? (
                                    <ProfessionalRosterLines
                                      services={user.professional.enabled_services}
                                      schedules={user.professional.schedules}
                                      restrictProfessionalServices={
                                        tenantSettings.restrict_professional_services
                                      }
                                      onMarkServices={() => {
                                        navigate(
                                          `/professionals?open=${user.professional?.id}&tab=enablement`
                                        );
                                      }}
                                    />
                                  ) : null}
                                </td>
                                <td className="text-muted">{formatUnitsLabel(user)}</td>
                                <td className="text-end">
                                  {isPendingInvite ? (
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      className="me-1"
                                      title={resendTitle}
                                      disabled={isResending || resendCooldownMs > 0}
                                      onClick={() => {
                                        void handleResendInvite(user);
                                      }}
                                    >
                                      {isResending ? (
                                        <Spinner size="sm" />
                                      ) : resendCooldownMs > 0 ? (
                                        <span className="px-1">{resendCooldownSeconds}s</span>
                                      ) : (
                                        <i className="ri-mail-send-line" />
                                      )}
                                    </Button>
                                  ) : null}
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    title={t("users.actions.edit")}
                                    onClick={() => openEdit(user)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    title={t("users.actions.delete")}
                                    onClick={() => {
                                      setDeleteTarget(user);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("users.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("users.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("users.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        className="border-0"
      >
        <OffcanvasHeader toggle={closeOffcanvas} className="bg-light">
          {editingUser ? t("users.form.editTitle") : t("users.actions.invite")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {!editingUser ? (
            <Alert color="info" className="mb-3">
              {t("users.invite.modeHint")}
            </Alert>
          ) : null}
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <Form
            key={entityFormKey(editingUser?.id)}
            onSubmit={(event) => {
              event.preventDefault();
              formik.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label htmlFor="user-name">{t("users.fields.name.label")}</Label>
              <Input
                id="user-name"
                name="name"
                placeholder={t("users.fields.name.placeholder")}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              {formik.touched.name && formik.errors.name ? (
                <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="user-email">{t("users.fields.email.label")}</Label>
              <Input
                id="user-email"
                name="email"
                type="email"
                placeholder={t("users.fields.email.placeholder")}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.email && formik.errors.email)}
              />
              {formik.touched.email && formik.errors.email ? (
                <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
              ) : null}
            </div>

            {editingUser ? (
              <div className="mb-3">
                <Label htmlFor="user-password">
                  {t("users.fields.password.editLabel")}
                </Label>
                <Input
                  id="user-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("users.fields.password.editPlaceholder")}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.password && formik.errors.password)}
                />
                {formik.touched.password && formik.errors.password ? (
                  <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                ) : null}
              </div>
            ) : null}

            <div className="mb-3">
              <Label>{t("users.fields.roles.label")}</Label>
              <div className="d-flex flex-column gap-2">
                {roleOptions.map((option) => (
                  <div className="form-check" key={option.value}>
                    <Input
                      type="radio"
                      className="form-check-input"
                      id={`role-${option.value}`}
                      name="roles"
                      checked={formik.values.roles[0] === option.value}
                      onChange={() => selectRole(option.value)}
                    />
                    <Label className="form-check-label" htmlFor={`role-${option.value}`}>
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
              {formik.touched.roles && formik.errors.roles ? (
                <div className="text-danger small mt-1">{formik.errors.roles}</div>
              ) : null}
            </div>

            <RolePermissionsSummary
              matrix={permissionMatrix}
              roleName={selectedRole}
              loading={loadingPermissions}
              loadError={permissionsLoadError}
            />

            {isProfessionalRole && !editingUser ? (
              <div className="mb-3">
                <Label className="d-block">
                  {t("studio.users.fields.professional_mode.label")}
                </Label>
                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="form-check">
                    <Input
                      type="radio"
                      className="form-check-input"
                      id="professional-mode-create"
                      checked={professionalLinkMode === "create"}
                      onChange={() => {
                        setProfessionalLinkMode("create");
                        formik.setFieldValue("professional_id", null);
                      }}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="professional-mode-create"
                    >
                      {t("studio.users.fields.professional_mode.create")}
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      className="form-check-input"
                      id="professional-mode-link"
                      checked={professionalLinkMode === "link"}
                      onChange={() => {
                        setProfessionalLinkMode("link");
                        setPendingCreatePhoto(null);
                      }}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="professional-mode-link"
                    >
                      {t("studio.users.fields.professional_mode.link")}
                    </Label>
                  </div>
                </div>
                {professionalLinkMode === "create" ? (
                  <p className="text-muted small mb-0">
                    {t("studio.users.fields.professional_mode.createHint")}
                  </p>
                ) : loadingProfessionals ? (
                  <div className="py-2">
                    <Spinner size="sm" color="primary" className="me-2" />
                    {t("studio.users.loadingProfessionals")}
                  </div>
                ) : linkableProfessionals.length === 0 ? (
                  <p className="text-muted small mb-0">
                    {t("studio.users.empty.noProfessionals")}
                  </p>
                ) : (
                  <>
                    <Label htmlFor="user-professional">
                      {t("studio.users.fields.professional_id.label")}
                    </Label>
                    <Input
                      id="user-professional"
                      name="professional_id"
                      type="select"
                      value={formik.values.professional_id ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        formik.setFieldValue(
                          "professional_id",
                          value === "" ? null : Number(value)
                        );
                        formik.setFieldTouched("professional_id", true);
                      }}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.professional_id &&
                          formik.errors.professional_id
                        )
                      }
                    >
                      <option value="">
                        {t("studio.users.fields.professional_id.placeholder")}
                      </option>
                      {linkableProfessionals.map((professional) => (
                        <option key={professional.id} value={professional.id}>
                          {professional.name}
                        </option>
                      ))}
                    </Input>
                    {formik.touched.professional_id &&
                    formik.errors.professional_id ? (
                      <FormFeedback type="invalid">
                        {formik.errors.professional_id}
                      </FormFeedback>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            {isProfessionalRole && editingUser ? (
              <div className="mb-3">
                <Label htmlFor="user-professional">
                  {t("studio.users.fields.professional_id.label")}
                </Label>
                {loadingProfessionals ? (
                  <div className="py-2">
                    <Spinner size="sm" color="primary" className="me-2" />
                    {t("studio.users.loadingProfessionals")}
                  </div>
                ) : (
                  <Input
                    id="user-professional"
                    name="professional_id"
                    type="select"
                    value={formik.values.professional_id ?? ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      formik.setFieldValue(
                        "professional_id",
                        value === "" ? null : Number(value)
                      );
                      formik.setFieldTouched("professional_id", true);
                    }}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(formik.touched.professional_id && formik.errors.professional_id)
                    }
                  >
                    <option value="">
                      {t("studio.users.fields.professional_id.placeholder")}
                    </option>
                    {linkableProfessionals.map((professional) => (
                      <option key={professional.id} value={professional.id}>
                        {professional.name}
                      </option>
                    ))}
                  </Input>
                )}
                {formik.touched.professional_id && formik.errors.professional_id ? (
                  <FormFeedback type="invalid">
                    {formik.errors.professional_id}
                  </FormFeedback>
                ) : null}
              </div>
            ) : null}

            {isProfessionalRole &&
            professionalLinkMode === "create" &&
            !editingUser ? (
              <ProfessionalPhotoField
                name={formik.values.name}
                colorHex={DEFAULT_PROFESSIONAL_COLOR}
                photoUrl={pendingCreatePhotoPreview}
                busy={photoBusy}
                onPick={(file) => setPendingCreatePhoto(file)}
                onClear={() => setPendingCreatePhoto(null)}
                canClear={Boolean(pendingCreatePhoto)}
                cropMemoryKey={null}
              />
            ) : null}

            {isProfessionalRole && selectedProfessional ? (
              <ProfessionalPhotoField
                name={selectedProfessional.name}
                colorHex={selectedProfessional.color_hex}
                photoUrl={resolvePublicStorageUrl(selectedProfessional.photo_path)}
                busy={photoBusy}
                onPick={(file) => void handleUserFormPhotoPick(file)}
                onClear={() => void handleUserFormPhotoClear()}
                canClear={Boolean(selectedProfessional.photo_path)}
                cropMemoryKey={selectedProfessional.id}
              />
            ) : null}

            <div className="mb-3">
              <Label>{t("users.fields.units.label")}</Label>
              {loadingUnits ? (
                <div className="py-2">
                  <Spinner size="sm" color="primary" className="me-2" />
                  {t("users.loadingUnits")}
                </div>
              ) : availableUnits.length === 0 ? (
                <p className="text-muted small mb-0">{t("users.empty.noUnits")}</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {availableUnits.map((unit) => (
                    <div className="form-check" key={unit.id}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`unit-${unit.id}`}
                        checked={formik.values.units.includes(unit.id)}
                        onChange={() => toggleUnit(unit.id)}
                      />
                      <Label className="form-check-label" htmlFor={`unit-${unit.id}`}>
                        {unit.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              {formik.touched.units && formik.errors.units ? (
                <div className="text-danger small mt-1">{formik.errors.units}</div>
              ) : null}
            </div>

            <div className="d-flex gap-2 mt-4">
              <Button color="light" type="button" onClick={closeOffcanvas}>
                {t("users.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className={`${editingUser ? "ri-save-line" : "ri-mail-send-line"} align-bottom me-1`} />
                )}
                {editingUser ? t("users.actions.save") : t("users.actions.sendInvite")}
              </Button>
            </div>
          </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("users.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget
                  ? t("users.delete.description", { name: deleteTarget.name })
                  : t("users.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("users.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("users.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default UsersList;
